<script setup>
import {computed, onMounted, onUnmounted, ref} from "vue";
import {useForm} from "@inertiajs/inertia-vue3";
import DialogModal from "../../DialogModal.vue";
import SecondaryButton from "../../SecondaryButton.vue";
import PrimaryButton from "../../PrimaryButton.vue";
import {Inertia} from "@inertiajs/inertia";

const props = defineProps({
  formTitle: String,
  mediaInput: {
    type: Object,
    default() {
      return {'F': [], 'I': []};
    }
  },
  showCheckButton: {
    type: Boolean,
    default: false,
  },
  canSeeUploadForm: {
    type: Boolean,
    default() {
      return false;
    }
  },
  canModifyMediaDetails: {
    type: Boolean,
    default() {
      return false;
    }
  },
  hideSave: {
    type: Boolean,
    default() {
      return false;
    }
  },
  mediaType: {
    type: String,
    validator(value, props) {
      return ['I', 'F'].includes(value);
    }
  },
  mediaLocation: {
    type: String,
    validator(value, props) {
      return ['upload', 'element', 'production', 'sales', 'hr', 'operations', 'fill-order', 'accounting'].includes(value);
    }
  },
  saveEndpoint: {
    type: String,
    default() {
      return route('admin.media.store');
    }
  },
  extraAttributes: {
    type: Object,
    default() {
      return {};
    }
  },
  canSeeFiles: {
    type: Boolean,
    default() {
      return true;
    }
  },
  showThumbnailButton: {
    type: Boolean,
    default: true,
  },
  showRotateButtons: {
    type: Boolean,
    default: true,
  }
});

const formatUploadTimestamp = (createddate) => {
  if (createddate == null || createddate === "") {
    return "";
  }
  const sec = Number(createddate);
  if (!Number.isFinite(sec)) {
    return "";
  }
  return new Date(sec * 1000).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const groupByPublicBatch = (items) => {
  const list = items ?? [];
  const byBatch = new Map();
  const standard = [];
  for (const m of list) {
    const bid = m.public_upload_batch_id;
    if (bid) {
      if (!byBatch.has(bid)) {
        byBatch.set(bid, []);
      }
      byBatch.get(bid).push(m);
    } else {
      standard.push(m);
    }
  }
  const batches = [];
  for (const [batchId, batchItems] of byBatch) {
    batchItems.sort((a, b) => (a.prjimageId ?? 0) - (b.prjimageId ?? 0));
    const ts = batchItems[0]?.createddate ?? 0;
    batches.push({batchId, items: batchItems, createddate: ts});
  }
  batches.sort((a, b) => Number(b.createddate) - Number(a.createddate));
  return {batches, standard};
};

const imageGroups = computed(() => groupByPublicBatch(props.mediaInput?.I));
const publicImageBatches = computed(() => imageGroups.value.batches);
const standardImages = computed(() => imageGroups.value.standard);
const hasPublicImageBatches = computed(() => publicImageBatches.value.length > 0);

const fileGroups = computed(() => groupByPublicBatch(props.mediaInput?.F));
const publicFileBatches = computed(() => fileGroups.value.batches);
const standardFiles = computed(() => fileGroups.value.standard);
const hasPublicFileBatches = computed(() => publicFileBatches.value.length > 0);

const emit = defineEmits(['uploaded']);

const selectMedia = ref(null);
const form = useForm({
  media: [],
  location: props.mediaLocation,
  extra: props.extraAttributes
});
const uploadMedia = () => {
  form.media = selectMedia.value.files;
  form.post(props.saveEndpoint, {
    replace: true,
    preserveScroll: true,
    onFinish() {
      form.reset();
      selectMedia.value.value = null;
      emit('uploaded');
    }
  });
}

const toggleChecked = (media) => {
  // Optimistically update the UI
  media.checked = !media.checked;

  // Call the backend to update the 'checked' property
  Inertia.post(route('admin.media.toggleChecked', { mediaid: media.prjimageId }), {
    checked: media.checked,
  }, {
    preserveScroll: true,
    onError: () => {
      // Revert the change if there was an error
      media.checked = !media.checked;
    },
  });
};

const deleting = ref({media: null, loading: false})
const openDeleteMediaModal = (media) => {
  deleting.value.media = media;
}

const closeDeletingMediaModal = () => {
  deleting.value.media = null;
  deleting.value.loading = false;
}
const removeMedia = (mediaId) => {
  deleting.value.loading = true;

  Inertia.post(route('admin.media.destroy', {mediaid: mediaId}), {_method: 'DELETE'}, {
    replace: true,
    preserveScroll: true,
    onFinish() {
      closeDeletingMediaModal();
      emit('uploaded');
    }
  });
}

const rotateClockwise = (mediaId) => {
  rotateImage(90, mediaId);
}

const rotateCounterClockwise = (mediaId) => {
  rotateImage(-90, mediaId);
}

const rotateImage = (angle, mediaId) => {
  Inertia.post(route('admin.media.rotate', {
    mediaid: mediaId
  }), {angle}, {
    preserveScroll: true
  });
}

const makeMainThumbnail = (mediaId) => {
  Inertia.post(route('admin.media.thumbnail', {
    mediaid: mediaId
  }), {}, {
    preserveScroll: true
  });
}
const preview = ref(null);
const openPreviewModal = (image) => {
  preview.value = image;
}

const closePreviewModal = () => {
  preview.value = null;
}

const dropArea = ref({active: false});
const events = ['dragenter', 'dragover', 'dragleave', 'drop']

onMounted(() => {
  events.forEach((eventName) => {
    document.body.addEventListener(eventName, preventDefaults)
  })
})

onUnmounted(() => {
  events.forEach((eventName) => {
    document.body.removeEventListener(eventName, preventDefaults)
  })
})

const dropMedia = (event) => {
  dropArea.value.active = false;
  selectMedia.value.files = event.dataTransfer.files;
  uploadMedia();
}

const preventDefaults = (e) => {
  e.preventDefault()
}

const isImageType = (media) => {
  return media.type === 'I';
}
const isFileType = (media) => {
  return media.type === 'F';
}
</script>

<template>
  <div class="px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md mb-5">
    <div class="sm:grid grid-cols-6 gap-6" v-if="!hideSave && canSeeUploadForm">
      <div class="col-span-6">
        <div class="flex justify-center items-center w-full">
          <label for="media-input" @drop="dropMedia"
                 @dragenter.prevent="dropArea.active = true"
                 @dragover.prevent="dropArea.active = true"
                 @dragleave.prevent="dropArea.active = false"
                 class="flex flex-col justify-center items-center w-full h-32 rounded-lg border-2 border-dashed cursor-pointer hover:bg-gray-100"
                 :class="dropArea.active ? ['border-gray-500', 'bg-gray-100'] : ['border-gray-300']">
            <div class="flex flex-col justify-center items-center pt-5 pb-6">
              <svg aria-hidden="true" class="mb-3 w-10 h-10 text-gray-400" fill="none" stroke="currentColor"
                   viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
              </svg>
              <p class="mb-2 text-sm text-gray-500"><span
                class="font-semibold">Click to upload</span> or drag and drop</p>
              <p class="text-xs text-gray-500">Max. File Size: 30MB</p>
            </div>
            <input @change="uploadMedia()" id="media-input" ref="selectMedia" class="hidden" type="file"
                   :multiple="true">
          </label>
        </div>
      </div>
    </div>
    <template v-if="hasPublicImageBatches">
      <p class="font-bold mt-5 mb-2">From public link</p>
      <div v-for="batch in publicImageBatches" :key="batch.batchId" class="mb-8 rounded-lg border border-gray-200 bg-gray-50/40 p-4">
        <p class="text-sm font-semibold text-gray-800 mb-3">{{ formatUploadTimestamp(batch.createddate) }}</p>
        <div class="grid grid-cols-2 md:grid-cols-6 gap-2 md:gap-3">
          <figure v-for="media in batch.items" :key="media.prjimageId" class="relative cursor-pointer min-h-32"
                  :class="((media.is_thumbnail === true) && canModifyMediaDetails) ? ['rounded-lg', 'border-blue-400', 'border-2'] : []"
          >
            <figcaption class="bg-gray-50/50 w-full">
              <div class="text-center mt-1 text-sm" v-text="media.file_name"></div>
              <div class="text-center text-xs text-gray-500">{{ formatUploadTimestamp(media.createddate) }}</div>
            </figcaption>
            <img :src="media.url" :alt="media.file_name" class="h-auto max-w-full rounded-lg cursor-pointer"
                 :class="((media.is_thumbnail === true) && canModifyMediaDetails) ? ['p-2'] : []"
                 @click="openPreviewModal(media)" v-if="isImageType(media)"/>
            <figcaption class="absolute bottom-2 bg-gray-50/50 w-full" v-if="(!hideSave && canModifyMediaDetails)">
              <div class="text-center mt-1 text-xl justify-between flex mx-3" v-if="isImageType(media)">
                <template v-if="showThumbnailButton && media.is_thumbnail === false">
                  <button type="button" class="fa fa-star" @click.prevent="makeMainThumbnail(media.prjimageId)"></button>
                </template>
                <template v-if="showRotateButtons">
                  <button type="button" class="fa fa-undo" @click.prevent="rotateClockwise(media.prjimageId)"></button>
                  <button type="button" class="fa fa-redo" @click.prevent="rotateCounterClockwise(media.prjimageId)"></button>
                </template>

                <button type="button" class="fa fa-trash text-red-500"
                        @click.prevent="openDeleteMediaModal(media)"></button>
                <button
                  v-if="showCheckButton"
                  :class="['fa', media.checked ? 'fa fa-check-circle text-green-500' : 'fa fa-circle text-gray-500']"
                  @click.prevent="toggleChecked(media)"
                ></button>
              </div>
            </figcaption>
          </figure>
        </div>
      </div>
    </template>

    <template v-if="standardImages.length">
      <p class="font-bold mt-5 mb-2">Images</p>
      <div class="grid grid-cols-2 md:grid-cols-6 gap-2 md:gap-3">
        <figure v-for="media in standardImages" :key="media.prjimageId" class="relative cursor-pointer min-h-32"
                :class="((media.is_thumbnail === true) && canModifyMediaDetails) ? ['rounded-lg', 'border-blue-400', 'border-2'] : []"
        >
          <figcaption class="bg-gray-50/50 w-full">
            <div class="text-center mt-1 text-sm" v-text="media.file_name"></div>
            <div class="text-center text-xs text-gray-500">{{ formatUploadTimestamp(media.createddate) }}</div>
          </figcaption>
          <img :src="media.url" :alt="media.file_name" class="h-auto max-w-full rounded-lg cursor-pointer"
               :class="((media.is_thumbnail === true) && canModifyMediaDetails) ? ['p-2'] : []"
               @click="openPreviewModal(media)" v-if="isImageType(media)"/>
          <figcaption class="absolute bottom-2 bg-gray-50/50 w-full" v-if="(!hideSave && canModifyMediaDetails)">
            <div class="text-center mt-1 text-xl justify-between flex mx-3" v-if="isImageType(media)">
              <template v-if="showThumbnailButton && media.is_thumbnail === false">
                <button type="button" class="fa fa-star" @click.prevent="makeMainThumbnail(media.prjimageId)"></button>
              </template>
              <template v-if="showRotateButtons">
                <button type="button" class="fa fa-undo" @click.prevent="rotateClockwise(media.prjimageId)"></button>
                <button type="button" class="fa fa-redo" @click.prevent="rotateCounterClockwise(media.prjimageId)"></button>
              </template>

              <button type="button" class="fa fa-trash text-red-500"
                      @click.prevent="openDeleteMediaModal(media)"></button>
              <button
                v-if="showCheckButton"
                :class="['fa', media.checked ? 'fa fa-check-circle text-green-500' : 'fa fa-circle text-gray-500']"
                @click.prevent="toggleChecked(media)"
              ></button>
            </div>
          </figcaption>
        </figure>
      </div>
    </template>

    <p v-if="!(mediaInput?.I?.length)" class="text-gray-700 mt-5">No records...</p>
    <hr class="mt-5 mb-5 text-gray-200" v-if="canSeeFiles">
    <template v-if="canSeeFiles && hasPublicFileBatches">
      <p class="font-bold mb-2">From public link</p>
      <div v-for="batch in publicFileBatches" :key="'f-' + batch.batchId" class="mb-8 rounded-lg border border-gray-200 bg-gray-50/40 p-4">
        <p class="text-sm font-semibold text-gray-800 mb-3">{{ formatUploadTimestamp(batch.createddate) }}</p>
        <div class="grid grid-cols-2 md:grid-cols-6 gap-2 md:gap-3">
          <figure v-for="media in batch.items" :key="media.prjimageId" class="relative cursor-pointer min-h-32"
                  :class="((media.is_thumbnail === true) && canModifyMediaDetails) ? ['rounded-lg', 'border-blue-400', 'border-2'] : []"
          >
            <figcaption class="bg-gray-50/50 w-full">
              <div class="text-center mt-1 text-sm" v-text="media.file_name"></div>
              <div class="text-center text-xs text-gray-500">{{ formatUploadTimestamp(media.createddate) }}</div>
            </figcaption>
            <a :href="media.url"
               class="flex text-7xl bg-gray-300 cursor-pointer justify-center items-center h-44 max-w-full rounded-lg"
               :title="`Download ${media.file_name}`" v-if="isFileType(media)">
              <span class="fa fa-file-download"></span>
            </a>
            <figcaption class="absolute bottom-2 bg-gray-50/50 w-full" v-if="(!hideSave && canModifyMediaDetails)">
              <div class="text-center mt-1 text-xl" v-if="isFileType(media)">
                <span class="fa fa-trash text-red-500 cursor-pointer text-xl"
                      @click.prevent="openDeleteMediaModal(media)"></span>
              </div>
            </figcaption>
          </figure>
        </div>
      </div>
    </template>

    <template v-if="canSeeFiles && standardFiles.length">
      <p class="font-bold mb-2">Files</p>
      <div class="grid grid-cols-2 md:grid-cols-6 gap-2 md:gap-3">
        <figure v-for="media in standardFiles" :key="media.prjimageId" class="relative cursor-pointer min-h-32"
                :class="((media.is_thumbnail === true) && canModifyMediaDetails) ? ['rounded-lg', 'border-blue-400', 'border-2'] : []"
        >
          <figcaption class="bg-gray-50/50 w-full">
            <div class="text-center mt-1 text-sm" v-text="media.file_name"></div>
            <div class="text-center text-xs text-gray-500">{{ formatUploadTimestamp(media.createddate) }}</div>
          </figcaption>
          <a :href="media.url"
             class="flex text-7xl bg-gray-300 cursor-pointer justify-center items-center h-44 max-w-full rounded-lg"
             :title="`Download ${media.file_name}`" v-if="isFileType(media)">
            <span class="fa fa-file-download"></span>
          </a>
          <figcaption class="absolute bottom-2 bg-gray-50/50 w-full" v-if="(!hideSave && canModifyMediaDetails)">
            <div class="text-center mt-1 text-xl" v-if="isFileType(media)">
              <span class="fa fa-trash text-red-500 cursor-pointer text-xl"
                    @click.prevent="openDeleteMediaModal(media)"></span>
            </div>
          </figcaption>
        </figure>
      </div>
    </template>

    <p v-if="canSeeFiles && !(mediaInput?.F?.length)" class="text-gray-700">No records...</p>

  </div>
  <DialogModal :show="preview !== null" @close="closePreviewModal" max-width="7xl">
    <template #title>
      <b v-text="preview.file_name"></b>
    </template>

    <template #content>
      <img :src="preview.url" :alt="preview.file_name">
    </template>
  </DialogModal>

  <DialogModal :show="deleting.media !== null" @close="closeDeletingMediaModal">
    <template #title>
      <b>Please confirm</b>
    </template>

    <template #content>
      You are about to delete <b v-text="deleting.media.file_name"></b>.
      <br>Are you sure?
    </template>

    <template #footer>
      <SecondaryButton @click="closeDeletingMediaModal" class="mr-2" :disabled="deleting.loading">
        Cancel
      </SecondaryButton>
      <PrimaryButton @click="removeMedia(deleting.media.prjimageId)" :disabled="deleting.loading">
        Confirm
      </PrimaryButton>
    </template>
  </DialogModal>
</template>

<style scoped>

</style>
